"use client"

import { Fragment } from "react"
import { Dialog, Transition } from "@headlessui/react"
import {
  X,
  Receipt,
  FileText,
  User,
  Calendar,
  DollarSign,
  ArrowDownRight,
  ArrowUpRight,
} from "lucide-react"
import { useCurrentUser } from "../../utils/context/UserProvider"

const ExpenseDetailModal = ({ isOpen, onClose, expense }) => {
  const { currentUser } = useCurrentUser()
  const isClient = currentUser?.role?.name === "Client"

  if (!expense) return null

  const direction = expense.direction || "debit"
  const isCredit = direction === "credit"

  // Format currency
  const formatCurrency = (amount, currency = "USD") => {
    const num = Number(amount) || 0
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(num)
  }

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
  }

  // Flow badge
  const getDirectionMeta = () => {
    if (isCredit) {
      return {
        label: "Credit (Adjustment)",
        className:
          "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300",
        Icon: ArrowUpRight,
      }
    }
    return {
      label: "Debit (Expense)",
      className: "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300",
      Icon: ArrowDownRight,
    }
  }

  const dirMeta = getDirectionMeta()

  // numeric values
  const expenseAmount = Number.parseFloat(expense.amount) || 0
  const previousBalance = Number.parseFloat(expense.source_balance_before) || 0
  const currentBalance = Number.parseFloat(expense.source_balance_after) || 0

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-xl bg-white dark:bg-neutral-950 shadow-xl transition-all border border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30">
                {/* Header */}
                <div className="bg-gradient-to-r from-neutral-50 to-neutral-100 dark:from-neutral-800 dark:to-neutral-900 border-b border-neutral-200 dark:border-neutral-900 p-4">
                  <div className="flex items-center justify-between">
                    <Dialog.Title className="text-lg font-medium text-neutral-900 dark:text-white flex items-center gap-2">
                      <Receipt className="h-5 w-5 text-neutral-500 dark:text-neutral-400" />
                      Asset Details
                    </Dialog.Title>
                    <button
                      onClick={onClose}
                      className="p-1.5 rounded-full text-neutral-500 hover:bg-neutral-200/50 dark:text-neutral-400 dark:hover:bg-neutral-700/50 transition-colors"
                      type="button"
                      aria-label="Close"
                    >
                      <X size={20} />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5 space-y-5">
                  {/* Summary */}
                  <div className="text-center pb-4">
                    <div
                      className={`inline-flex items-center justify-center w-12 h-12 rounded-full mb-3 ${
                        isCredit ? "bg-green-50 dark:bg-green-900/20" : "bg-red-50 dark:bg-red-900/20"
                      }`}
                    >
                      {isCredit ? (
                        <ArrowUpRight className="h-6 w-6 text-green-500 dark:text-green-400" />
                      ) : (
                        <ArrowDownRight className="h-6 w-6 text-red-500 dark:text-red-400" />
                      )}
                    </div>
                    <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-2">
                      {expense.title}
                    </h3>
                    <div
                      className={`text-2xl font-semibold mb-3 ${
                        isCredit ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
                      }`}
                    >
                      {isCredit ? "+" : "-"}
                      {formatCurrency(expenseAmount, expense.credit_account?.account_currency || "USD")}
                    </div>
                    <div className="flex items-center justify-center gap-2">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${dirMeta.className}`}
                      >
                        <dirMeta.Icon className="h-3 w-3 mr-1" />
                        {dirMeta.label}
                      </span>
                      {expense.type && (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-neutral-50 text-neutral-700 dark:bg-neutral-900/20 dark:text-neutral-300">
                          {expense.type}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="border-t border-neutral-200 dark:border-neutral-900" />

                  {/* Details */}
                  <div className="space-y-4">
                    {/* Asset Code */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400">
                        <Receipt size={16} />
                        <span>Asset Code</span>
                      </div>
                      <div className="text-sm font-medium text-neutral-900 dark:text-white font-mono">
                        {expense.expenseCode}
                      </div>
                    </div>

                    {/* Client */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400">
                        <User size={16} />
                        <span>Client</span>
                      </div>
                      <div className="text-sm font-medium text-neutral-900 dark:text-white">
                        {expense.client?.reference || expense.client?.fullName || "N/A"}
                      </div>
                    </div>

                    {/* Date */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400">
                        <Calendar size={16} />
                        <span>Date</span>
                      </div>
                      <div className="text-sm font-medium text-neutral-900 dark:text-white">
                        {formatDate(expense.createdAt)}
                      </div>
                    </div>

                    {/* Processed By - Only admins see */}
                    {!isClient && (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400">
                          <User size={16} />
                          <span>Processed By</span>
                        </div>
                        <div className="text-sm font-medium text-neutral-900 dark:text-white">
                          {expense.admin?.fullName || expense.admin?.name || "System"}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Notes */}
                  {expense.note && (
                    <div className="pt-2">
                      <div className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400 mb-2">
                        <FileText size={16} />
                        <span>Notes</span>
                      </div>
                      <p className="text-sm text-neutral-700 dark:text-neutral-300 bg-neutral-50 dark:bg-neutral-900/50 p-3 rounded-xl border border-neutral-200 dark:border-neutral-900">
                        {expense.note}
                      </p>
                    </div>
                  )}

                  {/* Balance Impact */}
                  <div className="mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-900">
                    <div className="flex items-center gap-2 text-sm font-medium text-neutral-800 dark:text-neutral-200 mb-3">
                      {isCredit ? (
                        <ArrowUpRight size={16} className="text-green-500 dark:text-green-400" />
                      ) : (
                        <ArrowDownRight size={16} className="text-red-500 dark:text-red-400" />
                      )}
                      <span>Balance Impact</span>
                    </div>
                    <div className="bg-neutral-50 dark:bg-neutral-900/50 rounded-xl border border-neutral-200 dark:border-neutral-900 p-3">
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-neutral-600 dark:text-neutral-400">
                            Previous Balance:
                          </span>
                          <span className="font-medium text-neutral-900 dark:text-white">
                            {formatCurrency(
                              previousBalance,
                              expense.credit_account?.account_currency || "USD"
                            )}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-neutral-600 dark:text-neutral-400">
                            {isCredit ? "Credit (Adjustment) Amount:" : "Debit (Expense) Amount:"}
                          </span>
                          <span
                            className={`font-medium ${
                              isCredit ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
                            }`}
                          >
                            {isCredit ? "+" : "-"}
                            {formatCurrency(
                              expenseAmount,
                              expense.credit_account?.account_currency || "USD"
                            )}
                          </span>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-neutral-200 dark:border-neutral-800">
                          <span className="font-medium text-neutral-900 dark:text-white">
                            Current Balance:
                          </span>
                          <span className="font-semibold text-neutral-900 dark:text-white">
                            {formatCurrency(
                              currentBalance,
                              expense.credit_account?.account_currency || "USD"
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="border-t border-neutral-200 dark:border-neutral-900 p-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="w-full px-4 py-2.5 text-sm font-medium text-neutral-700 bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-900/40 dark:text-neutral-300 dark:hover:bg-neutral-700 rounded-xl transition-colors"
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default ExpenseDetailModal
